import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { deal, shuffle, standardDeck, type Card } from '@campfire/cards';
import { followable, newTable, playCard, sortHand, turnOf, type CompletedTrick, type Trick, type TrickRules, type TrickTable } from '@campfire/tricks';

export interface HeartsOptions {
  /** Pass three cards before each deal (rotating left, right, across, hold). */
  readonly passing: boolean;
  /** Shooting the moon: everyone else +26, or the shooter −26. */
  readonly moon: 'others' | 'self';
  /** The game ends once someone reaches this score; lowest wins. */
  readonly target: number;
}

export type PassDir = 'left' | 'right' | 'across' | 'hold';

export type HeartsEvent =
  | { readonly kind: 'passed'; readonly dir: PassDir }
  | { readonly kind: 'hand'; readonly hand: number; readonly points: readonly number[]; readonly moon: Seat | null };

export interface HeartsState {
  readonly options: HeartsOptions;
  readonly phase: 'pass' | 'play';
  readonly hand: number;
  readonly passDir: PassDir;
  /** Chosen pass per seat (secret until exchanged); null = not chosen yet. */
  readonly passes: readonly (readonly string[] | null)[];
  /** Cards each seat received in the last exchange (shown only to the receiver). */
  readonly received: readonly (readonly string[])[];
  readonly table: TrickTable;
  readonly firstTrick: boolean;
  readonly heartsBroken: boolean;
  readonly scores: readonly number[];
  readonly last: HeartsEvent | null;
  readonly result: Outcome | null;
}

export type HeartsAction = { readonly type: 'pass'; readonly cards: readonly string[] } | { readonly type: 'play'; readonly card: string };

export interface HeartsView {
  readonly you: Seat | null;
  readonly phase: 'pass' | 'play';
  readonly hand: readonly Card[] | null;
  /** Card ids you may play now. */
  readonly legal: readonly string[];
  readonly handCounts: readonly number[];
  readonly passDir: PassDir;
  /** Whether each seat has chosen its pass. */
  readonly passed: readonly boolean[];
  /** Your chosen pass while waiting for the others. */
  readonly yourPass: readonly string[] | null;
  /** Card ids you were just passed. */
  readonly received: readonly string[];
  readonly turn: Seat | null;
  readonly trick: Trick;
  readonly lastTrick: CompletedTrick | null;
  readonly won: readonly number[];
  /** Penalty points taken so far this hand. */
  readonly handPoints: readonly number[];
  readonly heartsBroken: boolean;
  readonly firstTrick: boolean;
  readonly scores: readonly number[];
  readonly target: number;
  readonly handNo: number;
  readonly last: HeartsEvent | null;
  readonly result: Outcome | null;
}

const RULES: TrickRules = { trump: null };
const TWO_CLUBS = '2C#0';
const QUEEN_SPADES = 'QS#0';

/** Cards removed so the deck deals evenly: 3 → 17 each, 5 → 10, 6 → 8. The 2♣ always stays. */
const REMOVED: Readonly<Record<number, readonly string[]>> = {
  3: ['2D#0'],
  4: [],
  5: ['2D#0', '2S#0'],
  6: ['2D#0', '2S#0', '3D#0', '3S#0'],
};

export function heartsDeck(seats: number): Card[] {
  const drop = REMOVED[seats] ?? [];
  return standardDeck().filter((c) => !drop.includes(c.id));
}

export const cardPoints = (c: Card): number => (c.suit === 'H' ? 1 : c.id === QUEEN_SPADES ? 13 : 0);
const isPoint = (c: Card) => cardPoints(c) > 0;

/** Pass rotation; "across" only when there is a seat directly across (even tables). */
export function passDirFor(hand: number, seats: number): PassDir {
  const cycle: PassDir[] = seats % 2 === 0 ? ['left', 'right', 'across', 'hold'] : ['left', 'right', 'hold'];
  return cycle[(hand - 1) % cycle.length] as PassDir;
}

export function passTarget(seat: Seat, dir: PassDir, seats: number): Seat {
  const k = dir === 'left' ? 1 : dir === 'right' ? seats - 1 : dir === 'across' ? seats / 2 : 0;
  return (seat + k) % seats;
}

const holderOf2C = (hands: readonly (readonly Card[])[]): Seat => hands.findIndex((h) => h.some((c) => c.id === TWO_CLUBS));

function newHand(options: HeartsOptions, seats: number, hand: number, scores: readonly number[], rng: Rng, last: HeartsEvent | null): HeartsState {
  const { hands } = deal(shuffle(heartsDeck(seats), rng), seats, { perHand: 'all' });
  const passDir = options.passing ? passDirFor(hand, seats) : 'hold';
  const sorted = hands.map((h) => sortHand(h, RULES));
  return {
    options,
    phase: passDir === 'hold' ? 'play' : 'pass',
    hand,
    passDir,
    passes: sorted.map(() => null),
    received: sorted.map(() => []),
    table: newTable(sorted, holderOf2C(sorted)),
    firstTrick: true,
    heartsBroken: false,
    scores,
    last,
    result: null,
  };
}

/** Cards `seat` may play right now (FR-HEARTS-03/04/05). */
export function legalCards(s: HeartsState, seat: Seat): Card[] {
  const hand = s.table.hands[seat] ?? [];
  const t = s.table.trick;
  if (t.plays.length === 0) {
    if (s.firstTrick) return hand.filter((c) => c.id === TWO_CLUBS);
    if (s.heartsBroken) return [...hand];
    const nonHearts = hand.filter((c) => c.suit !== 'H');
    return nonHearts.length ? nonHearts : [...hand];
  }
  const options = followable(hand, t, RULES);
  if (s.firstTrick && options.some((c) => c.suit !== t.plays[0]?.card.suit)) {
    // Void in the led suit on the first trick: no points unless that is all you have.
    const clean = options.filter((c) => !isPoint(c));
    if (clean.length) return clean;
  }
  return options;
}

function handPoints(t: TrickTable): number[] {
  return t.taken.map((cs) => cs.reduce((p, c) => p + cardPoints(c), 0));
}

function endHand(s: HeartsState, rng: Rng): HeartsState {
  const raw = handPoints(s.table);
  const moon = raw.findIndex((p) => p === 26);
  const points = moon < 0 ? raw : raw.map((_, i) => (s.options.moon === 'others' ? (i === moon ? 0 : 26) : i === moon ? -26 : 0));
  const scores = s.scores.map((v, i) => v + (points[i] ?? 0));
  const last: HeartsEvent = { kind: 'hand', hand: s.hand, points, moon: moon < 0 ? null : moon };
  if (scores.some((v) => v >= s.options.target)) {
    const low = Math.min(...scores);
    const winners = scores.flatMap((v, i) => (v === low ? [i] : []));
    return { ...s, scores, last, result: { winners, draw: false, reason: 'target', scores: { ...scores } } };
  }
  return newHand(s.options, s.scores.length, s.hand + 1, scores, rng, last);
}

function chooseK<T>(items: readonly T[], k: number): T[][] {
  if (k === 0) return [[]];
  const out: T[][] = [];
  items.forEach((x, i) => {
    for (const rest of chooseK(items.slice(i + 1), k - 1)) out.push([x, ...rest]);
  });
  return out;
}

export const hearts: GameDefinition<HeartsState, HeartsAction, HeartsView, HeartsOptions> = {
  id: 'hearts',
  name: 'Hearts',
  rules: [
    'Avoid taking hearts and the queen of spades. Lowest score wins. Each hand is a series of tricks and you must follow suit when you can.',
    'The deck is dealt out evenly (a few low cards are removed for 3, 5 or 6 players). The player with the 2 of clubs leads it to start the first trick.',
    'Setting: Passing. By default everyone secretly passes 3 cards at the start of each hand: left, then right, then across (4 or 6 players), then no pass, and repeat. Turn it off to skip passing.',
    'Each heart is 1 point and the queen of spades is 13. You cannot lead a heart until hearts are broken (a heart has been played), unless all you hold is hearts.',
    'On the first trick you may not play a heart or the queen of spades unless you have nothing else. No other card is ever worth anything.',
    'Shooting the moon: a player who takes every heart and the queen of spades gets 0 and everyone else gets 26. Setting: Shooting the moon can instead give the shooter minus 26.',
    'Setting: Game length. The game ends at the end of the hand in which someone reaches 100 (or 50) points, and the lowest score wins. Ties share the win.',
    'The app enforces every rule above. There are no teams, and no jack of diamonds bonus or other variations.',
  ],
  minSeats: 3,
  maxSeats: 6,
  defaultOptions: { passing: true, moon: 'others', target: 100 },
  optionSpecs: [
    {
      key: 'target',
      label: 'Game length',
      help: 'The game ends when someone reaches this score. Lowest score wins.',
      choices: [
        { value: 100, label: 'To 100 points' },
        { value: 50, label: 'To 50 points' },
      ],
    },
    {
      key: 'passing',
      label: 'Passing',
      choices: [
        { value: true, label: 'Pass 3 cards (left, right, across, hold)' },
        { value: false, label: 'No passing' },
      ],
    },
    {
      key: 'moon',
      label: 'Shooting the moon',
      help: 'Taking every heart and the Q♠.',
      choices: [
        { value: 'others', label: 'Everyone else +26' },
        { value: 'self', label: 'Shooter −26' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    return newHand(options, seats, 1, Array.from({ length: seats }, () => 0), rng, null);
  },

  // Passing is simultaneous: everyone who has not chosen yet may act.
  activeSeats(s) {
    if (s.result) return [];
    if (s.phase === 'pass') return s.passes.flatMap((p, i) => (p === null ? [i] : []));
    return [turnOf(s.table)];
  },

  validate(s, a, seat) {
    const hand = s.table.hands[seat] ?? [];
    if (a.type === 'pass') {
      if (s.phase !== 'pass') return { ok: false, reason: 'no passing now' };
      if (s.passes[seat] !== null) return { ok: false, reason: 'you already passed' };
      const ids = Array.isArray(a.cards) ? a.cards : [];
      if (ids.length !== 3 || new Set(ids).size !== 3) return { ok: false, reason: 'pass exactly three cards' };
      if (!ids.every((id) => hand.some((c) => c.id === id))) return { ok: false, reason: 'you do not hold that card' };
      return { ok: true };
    }
    if (a.type !== 'play') return { ok: false, reason: 'unknown action' };
    if (s.phase !== 'play') return { ok: false, reason: 'pass your cards first' };
    if (seat !== turnOf(s.table)) return { ok: false, reason: 'not your turn' };
    if (!hand.some((c) => c.id === a.card)) return { ok: false, reason: 'you do not hold that card' };
    if (!legalCards(s, seat).some((c) => c.id === a.card)) {
      if (s.firstTrick && s.table.trick.plays.length === 0) return { ok: false, reason: 'the 2♣ leads the first trick' };
      if (s.table.trick.plays.length === 0) return { ok: false, reason: 'hearts are not broken yet' };
      return { ok: false, reason: s.firstTrick ? 'follow suit; no points on the first trick' : 'you must follow suit' };
    }
    return { ok: true };
  },

  apply(s, a, { actor, rng }) {
    if (a.type === 'pass') {
      const seat = actor as Seat;
      const passes = s.passes.map((p, i) => (i === seat ? [...a.cards] : p));
      if (passes.some((p) => p === null)) return { ...s, passes };
      const n = s.table.hands.length;
      const gifts = s.table.hands.map(() => [] as Card[]);
      const hands = s.table.hands.map((h, i) => {
        const out = new Set(passes[i] as string[]);
        (gifts[passTarget(i, s.passDir, n)] as Card[]).push(...h.filter((c) => out.has(c.id)));
        return h.filter((c) => !out.has(c.id));
      });
      const full = hands.map((h, i) => sortHand([...h, ...(gifts[i] as Card[])], RULES));
      return {
        ...s,
        phase: 'play',
        passes,
        received: gifts.map((g) => g.map((c) => c.id)),
        table: newTable(full, holderOf2C(full)),
        last: { kind: 'passed', dir: s.passDir },
      };
    }
    const seat = turnOf(s.table);
    const card = (s.table.hands[seat] as readonly Card[]).find((c) => c.id === a.card) as Card;
    const { table, completed } = playCard(s.table, seat, a.card, RULES, s.table.hands.length);
    const st: HeartsState = { ...s, table, heartsBroken: s.heartsBroken || card.suit === 'H', firstTrick: completed ? false : s.firstTrick };
    return completed && table.hands.every((h) => h.length === 0) ? endHand(st, rng) : st;
  },

  view(s, viewer: Viewer): HeartsView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const playing = !s.result && s.phase === 'play';
    const turn = playing ? turnOf(s.table) : null;
    return {
      you,
      phase: s.phase,
      hand: you === null ? null : (s.table.hands[you] ?? null),
      legal: you !== null && you === turn ? legalCards(s, you).map((c) => c.id) : [],
      handCounts: s.table.hands.map((h) => h.length),
      passDir: s.passDir,
      passed: s.passes.map((p) => p !== null),
      yourPass: you === null ? null : (s.passes[you] ?? null),
      received: you === null || s.phase !== 'play' ? [] : (s.received[you] ?? []),
      turn,
      trick: s.table.trick,
      lastTrick: s.table.lastTrick,
      won: s.table.won,
      handPoints: handPoints(s.table),
      heartsBroken: s.heartsBroken,
      firstTrick: s.firstTrick,
      scores: s.scores,
      target: s.options.target,
      handNo: s.hand,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result) return [];
    if (s.phase === 'pass') {
      if (s.passes[seat] !== null) return [];
      return chooseK((s.table.hands[seat] ?? []).map((c) => c.id), 3).map((cards) => ({ type: 'pass', cards }));
    }
    if (seat !== turnOf(s.table)) return [];
    return legalCards(s, seat).map((c) => ({ type: 'play', card: c.id }));
  },
};
